import axios from 'axios';
import api from '@/utils/api';
import apiConfig from '@/config/api';

/**
 * Service for handling media assets from the backend
 */
const mediaService = {
  // Simple in-memory cache for media URLs
  mediaCache: {
    urls: new Map(),
    versions: new Map(),
    
    // Cache a URL for a specific hash and version
    setUrl(hashId, version, url) {
      const key = `${hashId}:${version}`;
      this.urls.set(key, {
        url,
        timestamp: Date.now()
      });
    },
    
    // Get a cached URL if available and not expired
    getUrl(hashId, version) {
      const key = `${hashId}:${version}`;
      const cached = this.urls.get(key);
      
      if (cached) {
        // Expire cache after 10 minutes
        const isExpired = Date.now() - cached.timestamp > 10 * 60 * 1000;
        if (!isExpired) {
          return cached.url;
        }
        // Remove expired item
        this.urls.delete(key);
      }
      
      return null;
    },
    
    // Cache all versions for a hash
    setVersions(hashId, versions) {
      this.versions.set(hashId, {
        versions,
        timestamp: Date.now()
      });
    },
    
    // Get cached versions if available
    getVersions(hashId) {
      const cached = this.versions.get(hashId);
      
      if (cached) {
        // Expire cache after 10 minutes
        const isExpired = Date.now() - cached.timestamp > 10 * 60 * 1000;
        if (!isExpired) {
          return cached.versions;
        }
        // Remove expired item
        this.versions.delete(hashId);
      }
      
      return null;
    },
    
    // Clear the entire cache
    clear() {
      this.urls.clear();
      this.versions.clear();
    }
  },
  
  /**
   * Fix the media URL to work with the proxy
   * @param {string} url - The URL to fix
   * @returns {string} - The fixed URL
   */
  fixMediaUrl(url) {
    if (!url) return null;
    
    // If it's already a relative URL starting with / or a full URL with our domain, leave it
    if (url.startsWith('/') && !url.startsWith('//')) {
      return url;
    }
    
    // If it's a full URL, extract the path portion only
    if (url.startsWith('http')) {
      try {
        const urlObj = new URL(url);
        return urlObj.pathname; // This returns just the path without domain
      } catch (e) {
        console.error('Error parsing URL:', e);
      }
    }
    
    // If we get here, prepend / to make it a root-relative URL
    return url.startsWith('/') ? url : `/${url}`;
  },
  
  /**
   * Get media details from the backend API
   * @param {string} hashId - Hash ID of the media
   * @returns {Promise<Object>} Media data object
   */
  async getMediaDetails(hashId) {
    if (!hashId) {
      console.error('Empty media hash ID provided');
      return null;
    }
    
    try {
      const response = await axios.get(`/api/media/details/${hashId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching media details for ${hashId}:`, error);
      return null;
    }
  },
  
  /**
   * Get URL for a media file by its hash_id
   * @param {string} hashId - The media hash_id from Django
   * @param {string} version - Which version to retrieve
   * @returns {Promise<string|null>} The resolved URL or null if not found
   */
  async getMediaUrl(hashId, version = 'webp_medium') {
    if (!hashId) {
      console.warn('Empty hash_id provided to getMediaUrl');
      return null;
    }
    
    // If it's already a URL, return it
    if (hashId.startsWith('http') || hashId.startsWith('//') || hashId.startsWith('/media/')) {
      return hashId;
    }
    
    try {
      // Check if Django media API is available
      const url = `/api/media/${hashId}/`;
      const response = await axios.get(url);
      
      if (response.data?.url) {
        return response.data.url;
      } else if (response.data?.webp_versions) {
        const versions = response.data.webp_versions;
        return versions[version] || versions.medium || versions.original;
      }
      
      return null;
    } catch (error) {
      console.error(`Error fetching media ${hashId}:`, error);
      return null;
    }
  },
  
  /**
   * Get all versions of media by hash ID
   * @param {string} hashId - Media hash ID
   * @returns {Promise<Object>} Object containing URLs for all versions
   */
  async getMediaVersions(hashId) {
    try {
      const response = await axios.get(`/api/media/detail/${hashId}/`);
      return response.data.webp_versions || { url: response.data.url };
    } catch (error) {
      console.error(`Error getting media versions for ${hashId}:`, error);
      return null;
    }
  },
  
  /**
   * Get placeholder image of specified dimensions
   * @param {number} width - Width in pixels
   * @param {number} height - Height in pixels
   * @returns {Promise<string>} Placeholder URL
   */
  async getPlaceholder(width = 400, height = 300) {
    return apiConfig.getMediaUrl(`/api/media/placeholder/${width}/${height}/`);
  },
  
  /**
   * Get a properly sized media URL for a specific context
   * @param {string} mediaId - Media identifier
   * @param {string} context - Usage context (avatar, banner, thumbnail, etc)
   * @returns {string} Properly sized media URL
   */
  async getContextualMediaUrl(mediaId, context = 'default') {
    if (!mediaId) return null;
    
    try {
      // Use direct path format
      const response = await api.get(`/api/media/details/${mediaId}/`);
      
      if (response.data && response.data.url) {
        return apiConfig.getMediaUrl(response.data.url);
      }
      
      return null;
    } catch (error) {
      console.error(`Failed to load media ${mediaId} for context ${context}:`, error);
      
      // Try a fallback URL format if the API fails
      return apiConfig.getMediaUrl(`/media/cache/${context}/${mediaId}/default.webp`);
    }
  },
  
  /**
   * Debug helper for troubleshooting media resolution issues
   * @param {string} hashId - The hash_id to debug
   * @returns {Promise<Object>} - Detailed debug information
   */
  async debugMediaResolution(hashId) {
    if (!hashId) {
      console.error('Cannot debug empty media reference');
      return { error: 'Empty media reference' };
    }
    
    console.log(`[Media Debug] Starting debug for media: ${hashId}`);
    
    const debugInfo = {
      hashId,
      startTime: new Date().toISOString(),
      apiResponses: [],
      errors: [],
      resolvedUrls: {}
    };
    
    try {
      // Try to get media details
      console.log(`[Media Debug] Fetching media details from /api/media/${hashId}/`);
      try {
        const response = await axios.get(`/api/media/${hashId}/`);
        debugInfo.apiResponses.push({
          endpoint: `/api/media/${hashId}/`,
          status: response.status,
          data: response.data
        });
        
        // Try to extract URLs
        const media = response.data;
        
        // Check webp_versions
        if (media.webp_versions) {
          debugInfo.resolvedUrls.fromWebpVersions = media.webp_versions;
        }
        
        // Check individual fields
        const versions = ['webp_thumbnail', 'webp_medium', 'webp_large', 'webp_full', 'original'];
        versions.forEach(version => {
          if (media[version]) {
            debugInfo.resolvedUrls[version] = media[version];
          }
        });
        
        // Check if we have any URLs
        const hasUrls = Object.keys(debugInfo.resolvedUrls).length > 0;
        debugInfo.success = hasUrls;
        debugInfo.message = hasUrls ? 'Successfully resolved media URLs' : 'No media URLs found in response';
        
      } catch (error) {
        console.error(`[Media Debug] API error: ${error.message}`);
        debugInfo.errors.push({
          phase: 'api_request',
          message: error.message,
          stack: error.stack
        });
        
        // Try a different endpoint if it's a 404
        if (error.response && error.response.status === 404) {
          debugInfo.errors.push({
            phase: 'fallback_attempt',
            message: 'Trying fallback endpoints'
          });
          
          // Try debug endpoint if available
          try {
            const debugResponse = await axios.get(`/api/media/debug/${hashId}/`);
            debugInfo.apiResponses.push({
              endpoint: `/api/media/debug/${hashId}/`,
              status: debugResponse.status,
              data: debugResponse.data
            });
          } catch (debugError) {
            debugInfo.errors.push({
              phase: 'debug_endpoint',
              message: debugError.message
            });
          }
        }
      }
    } catch (error) {
      console.error('[Media Debug] Fatal error:', error);
      debugInfo.fatalError = {
        message: error.message,
        stack: error.stack
      };
    }
    
    debugInfo.endTime = new Date().toISOString();
    console.log('[Media Debug] Debug results:', debugInfo);
    return debugInfo;
  },
  
  /**
   * Create a media URL that works with the development proxy
   * @param {string} mediaId - Media ID or hash
   * @param {string} version - Version to request
   * @returns {string} URL that works with proxy
   */
  getProxiedMediaUrl(mediaId, version = 'medium') {
    // This will create URLs that go through our Vue proxy configuration
    return `/api/media/${mediaId}/${version}/`;
  },
  
  /**
   * Debug media URL to help diagnose issues
   * @param {string} url - URL to debug
   * @returns {Object} Debug information
   */
  async debugMediaUrl(url) {
    const debug = {
      original: url,
      isAbsolute: url.startsWith('http'),
      isMediaPath: url.includes('/media/'),
      host: window.location.host,
      port: window.location.port
    };
    
    try {
      const response = await fetch(url, { method: 'HEAD' });
      debug.status = response.status;
      debug.ok = response.ok;
      debug.contentType = response.headers.get('content-type');
    } catch (error) {
      debug.error = error.message;
    }
    
    console.table(debug);
    return debug;
  }
};

export default mediaService; 